import { Grid, TextField, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { DbUser } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import axios from "../../../../services/axios";
import { AlertState } from "../../../../types";
import { TechnicalIssuesMessage } from "../../utils/Toast";
import {
  SchoolCreationState,
  getError,
  initSchoolCreationState,
  useSchoolCreationState,
} from "../state/useSchoolCreationState";
import { School } from "@backend-types";
import { DistrictSearch } from "../utils/DistrictSearch";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import { AxiosError } from "axios";
import { generateGtmEvent } from "../../../../Helpers";

export type EditSchoolProps = {
  open: boolean;
  school?: School;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

type SchoolRequest = Pick<School, "name" | "district">;

const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));
export function EditSchool(props: EditSchoolProps) {
  const [state, dispatch] = useSchoolCreationState(props);
  const classes = useStyle();
  const updateForm = useCallback(
    (update: Partial<SchoolCreationState["form"]>) =>
      dispatch({ type: "updateForm", update }),
    [dispatch]
  );

  const formModified = () => {
    return !(
      state.form.name === props.school?.name &&
      (state.form.district ?? "") === (props.school?.district ?? "")
    );
  };

  const handleClose = () => {
    // generateGtmEvent({
    //   event: "modal",
    //   action: "close",
    //   name: "onclick",
    //   type: "click",
    //   region: "main content",
    //   section: "edit school",
    //   text: "close cross",
    // });
    props.onClose();
  };

  const submit = async () => {
    const { form } = state;
    const { name: schoolName } = form;
    generateGtmEvent({
      event: "form",
      action: "click",
      name: "onclick",
      type: "click",
      region: "main content",
      section: "edit school",
      text: "save",
    });
    try {
      await axios.post<SchoolRequest>("/schools", {
        name: schoolName,
        district: form.district,
        id: props.school?.id,
      });
      props.onSubmit({
        severity: "success",
        message: `Edited ${schoolName}`,
      });
      generateGtmEvent(
        {
          event: "form",
          action: "editing a school",
          name: "onsubmit",
          type: "submit",
          region: "main content",
          section: "edit school",
          text: "school edited",
        },
        { school: `${form.name}`, district: `${form.district}` }
      );
      dispatch({
        type: "replaceState",
        state: initSchoolCreationState(props.currentUser),
      });
    } catch (e: any) {
      if (
        e instanceof AxiosError &&
        typeof e.response?.data === "string" &&
        e.response?.data.includes("DuplicateSchoolNameException")
      ) {
        props.onSubmit({
          severity: "error",
          message: `${schoolName} already exists. Please use another name.`,
        });
      } else {
        props.onSubmit({
          severity: "error",
          message: TechnicalIssuesMessage(),
        });
      }
      throw e;
    }
  };

  useEffect(() => {
    if (props.open) {
      updateForm({
        name: props.school?.name,
        district: props.school?.district ?? "",
      });
    }
  }, [props.school, props.open, updateForm]);

  return (
    <UserManagementModal
      title="Edit school"
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      disableSubmit={!formModified()}
      errorText={getError(state)}>
      <Typography color="textSecondary" sx={{ marginBottom: "2rem" }}>
        Please enter the school name and district.
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            label="School"
            value={state.form.name}
            onChange={(e) => {
              generateGtmEvent({
                event: "form",
                action: "click",
                name: "onclick",
                type: "blur",
                region: "main content",
                section: "edit school ^ school",
                text: `${e.target.value}`,
              });
              updateForm({ name: e.target.value });
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <DistrictSearch
            selected={state.form.district}
            options={state.data.districts.value}
            onSelect={(e) => {
              updateForm({ district: e.target.value });
            }}
            onChange={(e) => {
              generateGtmEvent({
                event: "form",
                action: "click",
                name: "onclick",
                type: "blur",
                region: "main content",
                section: "edit school ^ district",
                text: `${e.target.value}`,
              });

              updateForm({ district: e.target.value });
            }}
            onAutoCompleteChange={(e, value) => {
              generateGtmEvent({
                event: "select",
                action: "click",
                name: "onclick",
                type: "select a district",
                region: "main content",
                section: "edit school ^ district",
                text: `${value}`,
              });
              updateForm({ district: value ?? "" });
            }}
          />
        </Grid>
      </Grid>
    </UserManagementModal>
  );
}
